"use client";

import { useState, useMemo, useEffect, useRef, useCallback } from "react";

declare global {
 interface Window {
 turnstile?: {
 render: (
 el: HTMLElement,
 opts: {
 sitekey: string;
 callback: (token: string) => void;
 appearance?: string;
 "response-field"?: boolean;
 }
 ) => string;
 reset: (widgetId: string) => void;
 };
 }
}

type ConnectLocation = {
 id: number;
 name: string;
 slug: string | null;
 address: string | null;
 city: string | null;
 state: string | null;
 zipCode: string | null;
 phone: string | null;
 timezone: string | null;
};

type OrgEventType = {
 id: number;
 title: string;
 slug: string;
 length: number;
 connectLocationId: number | null;
 teamSlug: string | null;
};

type Step = "select" | "matrix" | "booking" | "success";
type DatePreset = "this-week" | "next-week" | "two-weeks";

type SlotResult = {
 eventTypeId: number;
 locationId: number | null;
 slots: Record<string, { time: string }[]>;
};

type SelectedSlot = {
 time: string;
 locationId: number;
 locationName: string;
 locationAddress: string;
 eventTypeId: number;
 duration: number;
};

type BookingForm = {
 name: string;
 email: string;
 notes: string;
};

type BookingResult = {
 uid: string;
 title: string;
 startTime: string;
};

function pad(n: number) {
 return String(n).padStart(2, "0");
}

function isoToDate(iso: string): Date {
 return new Date(iso);
}

function formatDisplayTime(iso: string, timeZone: string): string {
 try {
 return new Date(iso).toLocaleTimeString("en-US", {
 hour: "numeric",
 minute: "2-digit",
 hour12: true,
 timeZone,
 });
 } catch {
 return iso;
 }
}

function formatDisplayDate(dateStr: string): string {
 try {
 const [year, month, day] = dateStr.split("-").map(Number);
 const d = new Date(year, month - 1, day);
 return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
 } catch {
 return dateStr;
 }
}

function formatShortDate(dateStr: string): string {
 try {
 const [year, month, day] = dateStr.split("-").map(Number);
 const d = new Date(year, month - 1, day);
 return d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
 } catch {
 return dateStr;
 }
}

function getDateRange(preset: DatePreset): { start: string; end: string } {
 const now = new Date();
 const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

 function toISO(d: Date) {
 return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T00:00:00.000Z`;
 }
 function toISOEnd(d: Date) {
 return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T23:59:59.999Z`;
 }

 if (preset === "this-week") {
 const end = new Date(today);
 end.setDate(today.getDate() + 7);
 return { start: toISO(today), end: toISOEnd(end) };
 }
 if (preset === "next-week") {
 const start = new Date(today);
 start.setDate(today.getDate() + 7);
 const end = new Date(today);
 end.setDate(today.getDate() + 14);
 return { start: toISO(start), end: toISOEnd(end) };
 }
 const end = new Date(today);
 end.setDate(today.getDate() + 14);
 return { start: toISO(today), end: toISOEnd(end) };
}

function getUserTimeZone(): string {
 try {
 return Intl.DateTimeFormat().resolvedOptions().timeZone;
 } catch {
 return "America/New_York";
 }
}

function groupLocationsByState(locations: ConnectLocation[]): Record<string, ConnectLocation[]> {
 const grouped: Record<string, ConnectLocation[]> = {};
 for (const loc of locations) {
 const key = loc.state ?? "Other";
 if (!grouped[key]) grouped[key] = [];
 grouped[key].push(loc);
 }
 return grouped;
}

function getUniqueTitles(orgEventTypes: OrgEventType[]): string[] {
 const seen = new Set<string>();
 const titles: string[] = [];
 for (const et of orgEventTypes) {
 if (!seen.has(et.title)) {
 seen.add(et.title);
 titles.push(et.title);
 }
 }
 return titles.sort();
}

export default function AvailabilityExplorer({
 locations,
 orgEventTypes,
 orgTeamId,
}: {
 locations: ConnectLocation[];
 orgEventTypes: OrgEventType[];
 orgTeamId: number;
}) {
 const [isOpen, setIsOpen] = useState(false);
 const [step, setStep] = useState<Step>("select");

 const [selectedLocationIds, setSelectedLocationIds] = useState<Set<number>>(new Set());
 const [selectedService, setSelectedService] = useState<string>("");
 const [datePreset, setDatePreset] = useState<DatePreset>("this-week");
 const [locationSearch, setLocationSearch] = useState("");

 const [loading, setLoading] = useState(false);
 const [slotResults, setSlotResults] = useState<SlotResult[]>([]);
 const [currentDateIndex, setCurrentDateIndex] = useState(0);

 const [selectedSlot, setSelectedSlot] = useState<SelectedSlot | null>(null);
 const [bookingForm, setBookingForm] = useState<BookingForm>({ name: "", email: "", notes: "" });
 const [bookingLoading, setBookingLoading] = useState(false);
 const [bookingError, setBookingError] = useState<string | null>(null);
 const [bookingResult, setBookingResult] = useState<BookingResult | null>(null);
 const [formLoadedAt, setFormLoadedAt] = useState<number>(0);
 const [cfTokenReady, setCfTokenReady] = useState(false);

 const cfTokenRef = useRef<string | null>(null);
 const turnstileWidgetRef = useRef<string | null>(null);
 const turnstileDivRef = useRef<HTMLDivElement>(null);

 const [timeZone, setTimeZone] = useState("UTC");
 useEffect(() => {
 setTimeZone(getUserTimeZone());
 }, []);
 const siteKey = process.env.NEXT_PUBLIC_CLOUDFLARE_SITEKEY ?? "";

 const uniqueServices = useMemo(() => getUniqueTitles(orgEventTypes), [orgEventTypes]);

 const locationsByState = useMemo(() => groupLocationsByState(locations), [locations]);

 const filteredLocations = useMemo(() => {
 if (!locationSearch.trim()) return locations;
 const q = locationSearch.toLowerCase();
 return locations.filter(
 (l) =>
 l.name.toLowerCase().includes(q) ||
 (l.city?.toLowerCase().includes(q) ?? false) ||
 (l.state?.toLowerCase().includes(q) ?? false) ||
 (l.zipCode?.includes(q) ?? false)
 );
 }, [locations, locationSearch]);

 const locationIdToEventTypeId = useMemo(() => {
 const map = new Map<number, number>();
 for (const r of slotResults) {
 if (r.locationId != null) map.set(r.locationId, r.eventTypeId);
 }
 return map;
 }, [slotResults]);

 const eventTypeDuration = useMemo(() => {
 if (!selectedService) return 30;
 const et = orgEventTypes.find((e) => e.title === selectedService);
 return et?.length ?? 30;
 }, [orgEventTypes, selectedService]);

 const slotMap = useMemo(() => {
 const map = new Map<number, Map<string, Set<string>>>();
 for (const result of slotResults) {
 const key = result.locationId ?? result.eventTypeId;
 const byDate = new Map<string, Set<string>>();
 for (const [date, times] of Object.entries(result.slots)) {
 byDate.set(date, new Set(times.map((t) => t.time)));
 }
 map.set(key, byDate);
 }
 return map;
 }, [slotResults]);

 const allDates = useMemo(() => {
 const dateSet = new Set<string>();
 for (const [, byDate] of slotMap) {
 for (const date of byDate.keys()) {
 if (byDate.get(date)!.size > 0) dateSet.add(date);
 }
 }
 return [...dateSet].sort();
 }, [slotMap]);

 const currentDate = allDates[currentDateIndex] ?? null;

 const timeSlotsForCurrentDate = useMemo(() => {
 if (!currentDate) return [];
 const timeSet = new Set<string>();
 for (const [, byDate] of slotMap) {
 const times = byDate.get(currentDate);
 if (times) for (const t of times) timeSet.add(t);
 }
 return [...timeSet].sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
 }, [slotMap, currentDate]);

 const bestPick = useMemo(() => {
 for (const date of allDates) {
 const times: string[] = [];
 for (const [, byDate] of slotMap) {
 const t = byDate.get(date);
 if (t) for (const iso of t) times.push(iso);
 }
 times.sort((a, b) => new Date(a).getTime() - new Date(b).getTime());
 for (const time of times) {
 for (const [locationId, byDate] of slotMap) {
 if (byDate.get(date)?.has(time)) {
 const loc = locations.find((l) => l.id === locationId);
 const etId = locationIdToEventTypeId.get(locationId);
 if (loc && etId != null) return { time, locationId: loc.id, eventTypeId: etId, locationName: loc.name };
 }
 }
 }
 }
 return null;
 }, [slotMap, allDates, locations, locationIdToEventTypeId]);

 const selectedLocationsList = useMemo(
 () => locations.filter((l) => selectedLocationIds.has(l.id)),
 [locations, selectedLocationIds]
 );

 const canCompare =
 selectedLocationIds.size >= 1 && !!selectedService;

 function openModal() {
 setStep("select");
 setSelectedLocationIds(new Set());
 setSelectedService(uniqueServices[0] ?? "");
 setDatePreset("this-week");
 setLocationSearch("");
 setSlotResults([]);
 setCurrentDateIndex(0);
 setSelectedSlot(null);
 setBookingForm({ name: "", email: "", notes: "" });
 setBookingError(null);
 setBookingResult(null);
 cfTokenRef.current = null;
 setIsOpen(true);
 }

 function closeModal() {
 setIsOpen(false);
 }

 function toggleLocation(id: number) {
 setSelectedLocationIds((prev) => {
 const next = new Set(prev);
 if (next.has(id)) {
 next.delete(id);
 } else {
 if (next.size >= 4) return prev;
 next.add(id);
 }
 return next;
 });
 }

 async function fetchCombinedAvailability() {
 if (selectedLocationIds.size === 0 || !selectedService) {
 setSlotResults([]);
 setStep("matrix");
 return;
 }

 const resolvedTz = getUserTimeZone();
 setTimeZone(resolvedTz);
 setLoading(true);
 setSlotResults([]);
 setCurrentDateIndex(0);

 const { start, end } = getDateRange(datePreset);

 try {
 const res = await fetch("/api/availability/combined", {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify({
 orgTeamId,
 locationIds: [...selectedLocationIds],
 serviceTitle: selectedService,
 startTime: start,
 endTime: end,
 timeZone: resolvedTz,
 }),
 });
 if (res.ok) {
 const data = await res.json();
 setSlotResults(data.results ?? []);
 }
 } catch (err) {
 console.error("Combined availability fetch failed:", err);
 } finally {
 setLoading(false);
 setStep("matrix");
 }
 }

 function selectSlot(time: string, locationId: number, eventTypeId: number) {
 const loc = locations.find((l) => l.id === locationId);
 if (!loc) return;
 const addressParts = [loc.address, loc.city, loc.state, loc.zipCode].filter(Boolean).join(", ");
 setSelectedSlot({
 time,
 locationId,
 locationName: loc.name,
 locationAddress: addressParts,
 eventTypeId,
 duration: eventTypeDuration,
 });
 setBookingError(null);
 setFormLoadedAt(Date.now());
 cfTokenRef.current = null;
 setCfTokenReady(false);
 setStep("booking");
 }

 useEffect(() => {
 if (step !== "booking" || !siteKey) return;

 const existingScript = document.getElementById("cf-turnstile-script");
 if (!existingScript) {
 const script = document.createElement("script");
 script.id = "cf-turnstile-script";
 script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js";
 script.async = true;
 script.onload = () => renderTurnstile();
 document.body.appendChild(script);
 } else {
 renderTurnstile();
 }

 function renderTurnstile() {
 if (turnstileDivRef.current && window.turnstile) {
 const id = window.turnstile.render(turnstileDivRef.current, {
 sitekey: siteKey,
 callback: (token: string) => {
 cfTokenRef.current = token;
 setCfTokenReady(true);
 },
 appearance: "interaction-only",
 "response-field": false,
 });
 turnstileWidgetRef.current = id;
 }
 }
 }, [step, siteKey]);

 const handleBook = useCallback(async () => {
 if (!selectedSlot) return;
 if (!bookingForm.name.trim() || !bookingForm.email.trim()) {
 setBookingError("Name and email are required.");
 return;
 }

 setBookingLoading(true);
 setBookingError(null);

 const endTime = new Date(
 new Date(selectedSlot.time).getTime() + selectedSlot.duration * 60 * 1000
 ).toISOString();

 try {
 const payload: Record<string, unknown> = {
 eventTypeId: selectedSlot.eventTypeId,
 start: selectedSlot.time,
 end: endTime,
 timeZone,
 language: "en",
 responses: {
 name: bookingForm.name.trim(),
 email: bookingForm.email.trim(),
 ...(bookingForm.notes.trim() ? { notes: bookingForm.notes.trim() } : {}),
 },
 metadata: {
 connectLocationId: String(selectedSlot.locationId),
 connectLocationName: selectedSlot.locationName,
 ...(selectedSlot.locationAddress ? { connectLocationAddress: selectedSlot.locationAddress } : {}),
 source: "availability-explorer",
 },
 _formLoadedAt: formLoadedAt,
 };

 if (cfTokenRef.current) {
 payload.cfToken = cfTokenRef.current;
 }

 const res = await fetch("/api/book/event", {
 method: "POST",
 headers: { "Content-Type": "application/json" },
 body: JSON.stringify(payload),
 });

 const data = await res.json();

 if (!res.ok) {
 const msg =
 data?.message ||
 (res.status === 409
 ? "This slot was just taken by another booking. Please go back and pick a different time."
 : res.status === 401
 ? "Security verification failed. Please wait for the check to complete and try again."
 : "Booking failed. Please try again.");
 setBookingError(msg);
 if (turnstileWidgetRef.current && window.turnstile) {
 window.turnstile.reset(turnstileWidgetRef.current);
 cfTokenRef.current = null;
 setCfTokenReady(false);
 }
 return;
 }

 setBookingResult({ uid: data.uid, title: data.title, startTime: data.startTime });
 setStep("success");
 } catch (err) {
 setBookingError("Something went wrong. Please try again.");
 } finally {
 setBookingLoading(false);
 }
 }, [selectedSlot, bookingForm, timeZone, formLoadedAt]);

 if (!isOpen) {
 if (locations.length < 2) return null;
 return (
 <button
 type="button"
 onClick={openModal}
 className="border-subtle bg-default hover:bg-subtle text-emphasis flex w-full items-center justify-center gap-2 rounded-lg border px-4 py-3 text-sm font-medium transition-colors">
 <svg
 xmlns="http://www.w3.org/2000/svg"
 width="16"
 height="16"
 viewBox="0 0 24 24"
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 strokeLinecap="round"
 strokeLinejoin="round">
 <circle cx="11" cy="11" r="8" />
 <path d="m21 21-4.35-4.35" />
 </svg>
 Find earliest appointment across locations
 <svg
 xmlns="http://www.w3.org/2000/svg"
 width="14"
 height="14"
 viewBox="0 0 24 24"
 fill="none"
 stroke="currentColor"
 strokeWidth="2"
 strokeLinecap="round"
 strokeLinejoin="round"
 className="text-subtle">
 <path d="m9 18 6-6-6-6" />
 </svg>
 </button>
 );
 }

 return (
 <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
 <div className="bg-black/50 absolute inset-0" onClick={closeModal} />

 <div className="bg-default relative z-10 flex w-full max-w-4xl flex-col rounded-2xl shadow-2xl"
 style={{ maxHeight: "90vh" }}>

 <div className="border-subtle flex shrink-0 items-center justify-between border-b px-6 py-4">
 <div className="flex items-center gap-3">
 {(step === "matrix" || step === "booking") && (
 <button
 type="button"
 onClick={() => {
 if (step === "booking") { setCfTokenReady(false); cfTokenRef.current = null; setStep("matrix"); }
 else { setStep("select"); setSlotResults([]); }
 }}
 className="text-subtle hover:text-emphasis flex items-center gap-1 text-sm transition-colors">
 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
 Back
 </button>
 )}
 <div>
 <h2 className="text-emphasis text-lg font-semibold">
 {step === "select" && "Find Your Appointment"}
 {step === "matrix" && "Available Appointments"}
 {step === "booking" && "Confirm Your Booking"}
 {step === "success" && "Booking Confirmed!"}
 </h2>
 {step === "matrix" && selectedService && (
 <p className="text-subtle text-sm">{selectedService} · {formatDatePresetLabel(datePreset)}</p>
 )}
 </div>
 </div>
 <button
 type="button"
 onClick={closeModal}
 className="text-subtle hover:text-emphasis rounded-lg p-1.5 transition-colors">
 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
 </button>
 </div>

 <div className="min-h-0 flex-1 overflow-y-auto">

 {step === "select" && (
 <div className="p-6 space-y-6">
 {uniqueServices.length > 1 && (
 <section>
 <h3 className="text-emphasis mb-3 text-sm font-semibold uppercase tracking-wide">
 1. Choose a Service
 </h3>
 <div className="flex flex-wrap gap-2">
 {uniqueServices.map((title) => (
 <button
 key={title}
 type="button"
 onClick={() => setSelectedService(title)}
 className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
 selectedService === title
 ? "border-blue-500 bg-blue-500 text-white"
 : "border-subtle text-emphasis hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
 }`}>
 {title}
 </button>
 ))}
 </div>
 </section>
 )}

 <section>
 <h3 className="text-emphasis mb-1 text-sm font-semibold uppercase tracking-wide">
 {uniqueServices.length > 1 ? "2." : "1."} Select Locations to Compare
 <span className="text-subtle ml-2 font-normal normal-case">
 {selectedLocationIds.size}/4 selected
 </span>
 </h3>
 <p className="text-subtle mb-3 text-xs">Compare availability across up to 4 locations simultaneously</p>

 {locations.length >= 6 && (
 <div className="mb-3">
 <input
 type="text"
 placeholder="Search locations..."
 value={locationSearch}
 onChange={(e) => setLocationSearch(e.target.value)}
 className="border-subtle bg-default text-default placeholder:text-muted w-full rounded-lg border px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
 />
 </div>
 )}

 <div className="border-subtle max-h-64 overflow-y-auto rounded-lg border">
 {locationSearch.trim() ? (
 <LocationCheckboxList
 locations={filteredLocations}
 selectedIds={selectedLocationIds}
 onToggle={toggleLocation}
 atMax={selectedLocationIds.size >= 4}
 />
 ) : (
 Object.entries(locationsByState).map(([state, locs]) => (
 <div key={state}>
 <div className="bg-subtle text-subtle sticky top-0 px-3 py-1.5 text-xs font-semibold uppercase tracking-wide">
 {state}
 </div>
 <LocationCheckboxList
 locations={locs}
 selectedIds={selectedLocationIds}
 onToggle={toggleLocation}
 atMax={selectedLocationIds.size >= 4}
 />
 </div>
 ))
 )}
 </div>

 {selectedLocationIds.size > 0 && (
 <div className="mt-2 flex flex-wrap gap-1.5">
 {selectedLocationsList.map((loc) => (
 <span key={loc.id} className="bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium">
 {loc.name}
 <button type="button" onClick={() => toggleLocation(loc.id)} className="ml-0.5 hover:text-blue-900">
 <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18M6 6l12 12" /></svg>
 </button>
 </span>
 ))}
 </div>
 )}
 </section>

 <section>
 <h3 className="text-emphasis mb-3 text-sm font-semibold uppercase tracking-wide">
 {uniqueServices.length > 1 ? "3." : "2."} Date Range
 </h3>
 <div className="flex flex-wrap gap-2">
 {(["this-week", "next-week", "two-weeks"] as DatePreset[]).map((preset) => (
 <button
 key={preset}
 type="button"
 onClick={() => setDatePreset(preset)}
 className={`rounded-full border px-4 py-1.5 text-sm font-medium transition-all ${
 datePreset === preset
 ? "border-blue-500 bg-blue-500 text-white"
 : "border-subtle text-emphasis hover:border-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950"
 }`}>
 {formatDatePresetLabel(preset)}
 </button>
 ))}
 </div>
 </section>
 </div>
 )}

 {step === "matrix" && (
 <div className="flex flex-col">
 {loading ? (
 <MatrixSkeleton locationCount={selectedLocationIds.size} />
 ) : allDates.length === 0 ? (
 <div className="py-16 text-center">
 <div className="text-subtle mb-2 text-4xl">📅</div>
 <p className="text-emphasis font-medium">No availability found</p>
 <p className="text-subtle mt-1 text-sm">Try a different date range or location combination</p>
 <button type="button" onClick={() => setStep("select")} className="mt-4 rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600">
 Adjust Search
 </button>
 </div>
 ) : (
 <>
 {bestPick && (
 <div className="border-b border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950/40 px-6 py-3">
 <div className="flex items-center justify-between">
 <div className="flex items-center gap-2">
 <span className="text-amber-500">⭐</span>
 <div>
 <span className="text-amber-800 dark:text-amber-200 text-sm font-semibold">Best pick — earliest available</span>
 <p className="text-amber-700 dark:text-amber-300 text-xs">
 {formatDisplayTime(bestPick.time, timeZone)} · {bestPick.locationName} ·{" "}
 {formatShortDate(bestPick.time.split("T")[0]!)}
 </p>
 </div>
 </div>
 <button
 type="button"
 onClick={() => selectSlot(bestPick.time, bestPick.locationId, bestPick.eventTypeId)}
 className="shrink-0 rounded-lg bg-amber-500 px-3 py-1.5 text-xs font-semibold text-white hover:bg-amber-600 transition-colors">
 Book this slot
 </button>
 </div>
 </div>
 )}

 <div className="border-subtle flex shrink-0 items-center justify-between border-b px-6 py-3">
 <button
 type="button"
 disabled={currentDateIndex === 0}
 onClick={() => setCurrentDateIndex((i) => Math.max(0, i - 1))}
 className="rounded-lg p-1.5 transition-colors disabled:opacity-30 hover:bg-subtle dark:hover:bg-gray-800">
 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6" /></svg>
 </button>
 <div className="text-center">
 <p className="text-emphasis text-sm font-semibold">{currentDate ? formatDisplayDate(currentDate) : ""}</p>
 <p className="text-subtle text-xs">{currentDateIndex + 1} of {allDates.length} available days</p>
 </div>
 <button
 type="button"
 disabled={currentDateIndex === allDates.length - 1}
 onClick={() => setCurrentDateIndex((i) => Math.min(allDates.length - 1, i + 1))}
 className="rounded-lg p-1.5 transition-colors disabled:opacity-30 hover:bg-subtle dark:hover:bg-gray-800">
 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
 </button>
 </div>

 <div className="overflow-x-auto">
 <table className="w-full min-w-max">
 <thead>
 <tr className="border-subtle border-b">
 <th className="bg-subtle w-28 px-4 py-3 text-left text-xs font-semibold text-muted">Time</th>
 {selectedLocationsList.map((loc) => {
 const etId = locationIdToEventTypeId.get(loc.id);
 const hasAny = etId !== undefined && (slotMap.get(loc.id)?.get(currentDate ?? "")?.size ?? 0) > 0;
 return (
 <th key={loc.id} className="bg-subtle px-3 py-3 text-center text-xs font-semibold">
 <div className="text-emphasis">{loc.name}</div>
 {loc.city && <div className="text-subtle font-normal">{loc.city}</div>}
 {!hasAny && etId !== undefined && (
 <div className="text-xs text-gray-400 font-normal">No availability</div>
 )}
 {etId === undefined && (
 <div className="text-xs text-gray-400 font-normal italic">Service N/A</div>
 )}
 </th>
 );
 })}
 </tr>
 </thead>
 <tbody>
 {timeSlotsForCurrentDate.length === 0 ? (
 <tr>
 <td colSpan={selectedLocationsList.length + 1} className="py-12 text-center text-sm text-gray-400">
 No time slots available on this day
 </td>
 </tr>
 ) : (
 timeSlotsForCurrentDate.map((timeIso) => {
 const isBestPick = bestPick?.time === timeIso;
 return (
 <tr
 key={timeIso}
 className={`border-subtle border-b transition-colors last:border-0 ${isBestPick ? "bg-amber-50/60 dark:bg-amber-950/20" : "hover:bg-subtle/50 dark:hover:bg-gray-800/30"}`}>
 <td className="px-4 py-2.5 text-sm font-medium text-default whitespace-nowrap">
 {isBestPick && <span className="mr-1 text-amber-500">⭐</span>}
 {formatDisplayTime(timeIso, timeZone)}
 </td>
 {selectedLocationsList.map((loc) => {
 const etId = locationIdToEventTypeId.get(loc.id);
 const available = etId !== undefined && slotMap.get(loc.id)?.get(currentDate ?? "")?.has(timeIso);
 return (
 <td key={loc.id} className="px-3 py-2.5 text-center">
 {available && etId !== undefined ? (
 <button
 type="button"
 onClick={() => selectSlot(timeIso, loc.id, etId)}
 className={`inline-flex items-center gap-1 rounded-lg px-3 py-1.5 text-xs font-semibold transition-all ${
 isBestPick
 ? "bg-amber-500 text-white hover:bg-amber-600 shadow-sm"
 : "bg-green-500 text-white hover:bg-green-600 shadow-sm"
 }`}>
 Book
 <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6" /></svg>
 </button>
 ) : (
 <span className="text-gray-300 dark:text-subtle text-sm">—</span>
 )}
 </td>
 );
 })}
 </tr>
 );
 })
 )}
 </tbody>
 </table>
 </div>
 </>
 )}
 </div>
 )}

 {step === "booking" && selectedSlot && (
 <div className="p-6">
 <div className="border-subtle bg-subtle mb-6 rounded-xl border p-4">
 <div className="flex items-start gap-3">
 <div className="bg-green-100 dark:bg-green-900 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl">
 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 dark:text-green-400"><rect width="18" height="18" x="3" y="4" rx="2" ry="2"/><line x1="16" x2="16" y1="2" y2="6"/><line x1="8" x2="8" y1="2" y2="6"/><line x1="3" x2="21" y1="10" y2="10"/></svg>
 </div>
 <div>
 <p className="text-emphasis font-semibold">{selectedService}</p>
 <p className="text-default mt-0.5 text-sm">
 {formatDisplayTime(selectedSlot.time, timeZone)} ·{" "}
 {formatDisplayDate(selectedSlot.time.split("T")[0] ?? "")}
 </p>
 <div className="text-subtle mt-1 flex items-center gap-1 text-xs">
 <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
 {selectedSlot.locationName}
 {selectedSlot.locationAddress && ` · ${selectedSlot.locationAddress}`}
 </div>
 <div className="text-subtle mt-0.5 flex items-center gap-1 text-xs">
 <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
 {selectedSlot.duration} minutes
 </div>
 </div>
 </div>
 </div>

 <div className="space-y-4">
 <div>
 <label className="text-emphasis mb-1.5 block text-sm font-medium">
 Your Name <span className="text-red-500">*</span>
 </label>
 <input
 type="text"
 value={bookingForm.name}
 onChange={(e) => setBookingForm((f) => ({ ...f, name: e.target.value }))}
 placeholder="John Doe"
 className="border-subtle bg-default text-default placeholder:text-muted w-full rounded-lg border px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
 />
 </div>
 <div>
 <label className="text-emphasis mb-1.5 block text-sm font-medium">
 Email Address <span className="text-red-500">*</span>
 </label>
 <input
 type="email"
 value={bookingForm.email}
 onChange={(e) => setBookingForm((f) => ({ ...f, email: e.target.value }))}
 placeholder="you@example.com"
 className="border-subtle bg-default text-default placeholder:text-muted w-full rounded-lg border px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
 />
 </div>
 <div>
 <label className="text-emphasis mb-1.5 block text-sm font-medium">
 Notes <span className="text-subtle font-normal">(optional)</span>
 </label>
 <textarea
 value={bookingForm.notes}
 onChange={(e) => setBookingForm((f) => ({ ...f, notes: e.target.value }))}
 placeholder="Any additional information..."
 rows={3}
 className="border-subtle bg-default text-default placeholder:text-muted w-full rounded-lg border px-4 py-2.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
 />
 </div>

 {siteKey && (
 <div ref={turnstileDivRef} className="flex justify-center" />
 )}

 {bookingError && (
 <div className="rounded-lg border border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950/40 px-4 py-3">
 <p className="text-red-700 dark:text-red-300 text-sm">{bookingError}</p>
 </div>
 )}
 </div>
 </div>
 )}

 {step === "success" && (
 <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
 <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-green-100 dark:bg-green-900">
 <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-green-600 dark:text-green-400"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
 </div>
 <h3 className="text-emphasis mb-2 text-2xl font-bold">Appointment Confirmed!</h3>
 {selectedSlot && (
 <div className="text-subtle space-y-1">
 <p className="text-base font-medium text-default">{selectedService}</p>
 <p>{formatDisplayTime(selectedSlot.time, timeZone)} · {formatDisplayDate(selectedSlot.time.split("T")[0] ?? "")}</p>
 <p className="flex items-center justify-center gap-1">
 <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/></svg>
 {selectedSlot.locationName}
 </p>
 </div>
 )}
 <p className="text-subtle mt-4 text-sm">A confirmation email has been sent to <strong>{bookingForm.email}</strong></p>
 <button
 type="button"
 onClick={closeModal}
 className="mt-8 rounded-xl bg-green-500 px-8 py-3 font-semibold text-white hover:bg-green-600 transition-colors">
 Done
 </button>
 </div>
 )}
 </div>

 {step === "select" && (
 <div className="border-subtle shrink-0 border-t px-6 py-4">
 <button
 type="button"
 disabled={!canCompare}
 onClick={fetchCombinedAvailability}
 className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-500 px-6 py-3 font-semibold text-white transition-all hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-40">
 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
 {selectedLocationIds.size === 0
 ? "Select at least 1 location to continue"
 : `See availability across ${selectedLocationIds.size} location${selectedLocationIds.size > 1 ? "s" : ""}`}
 {canCompare && (
 <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
 )}
 </button>
 {selectedLocationIds.size === 0 && (
 <p className="text-subtle mt-2 text-center text-xs">Check at least one location above to compare availability</p>
 )}
 </div>
 )}

 {step === "booking" && (
 <div className="border-subtle shrink-0 border-t px-6 py-4">
 <button
 type="button"
 disabled={bookingLoading || !bookingForm.name.trim() || !bookingForm.email.trim() || (!!siteKey && !cfTokenReady)}
 onClick={handleBook}
 className="flex w-full items-center justify-center gap-2 rounded-xl bg-blue-500 px-6 py-3 font-semibold text-white transition-all hover:bg-blue-600 disabled:cursor-not-allowed disabled:opacity-40">
 {bookingLoading ? (
 <>
 <svg className="animate-spin" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>
 Confirming...
 </>
 ) : (
 <>
 <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
 Confirm Booking
 </>
 )}
 </button>
 </div>
 )}
 </div>
 </div>
 );
}

function LocationCheckboxList({
 locations,
 selectedIds,
 onToggle,
 atMax,
}: {
 locations: ConnectLocation[];
 selectedIds: Set<number>;
 onToggle: (id: number) => void;
 atMax: boolean;
}) {
 return (
 <>
 {locations.map((loc) => {
 const isChecked = selectedIds.has(loc.id);
 const isDisabled = atMax && !isChecked;
 const addressParts = [loc.city, loc.state].filter(Boolean).join(", ");
 return (
 <label
 key={loc.id}
 className={`flex cursor-pointer items-center gap-3 border-subtle border-b px-3 py-2.5 last:border-0 transition-colors ${
 isDisabled ? "cursor-not-allowed opacity-40" : "hover:bg-subtle"
 } ${isChecked ? "bg-blue-50/60 dark:bg-blue-950/30" : ""}`}>
 <input
 type="checkbox"
 checked={isChecked}
 disabled={isDisabled}
 onChange={() => !isDisabled && onToggle(loc.id)}
 className="h-4 w-4 rounded border-gray-300 text-blue-500 focus:ring-blue-500"
 />
 <div className="min-w-0 flex-1">
 <p className="text-emphasis truncate text-sm font-medium">{loc.name}</p>
 {addressParts && <p className="text-subtle text-xs">{addressParts}</p>}
 </div>
 </label>
 );
 })}
 </>
 );
}

const SKELETON_PATTERN = [true, true, false, true, false, true];

function MatrixSkeleton({ locationCount }: { locationCount: number }) {
 const rows = 6;
 const cols = Math.max(locationCount, 1);
 return (
 <div className="animate-pulse p-6">
 <div className="mb-4 flex items-center justify-between">
 <div className="h-4 w-8 rounded bg-gray-200 " />
 <div className="h-5 w-40 rounded bg-gray-200 " />
 <div className="h-4 w-8 rounded bg-gray-200 " />
 </div>
 <div className="overflow-hidden rounded-lg border border-default">
 <div className="flex border-b border-default bg-subtle px-4 py-3 gap-4">
 <div className="h-4 w-16 rounded bg-gray-200 " />
 {Array.from({ length: cols }).map((_, i) => (
 <div key={i} className="flex-1 h-4 rounded bg-gray-200 " />
 ))}
 </div>
 {Array.from({ length: rows }).map((_, r) => (
 <div key={r} className="flex items-center gap-4 border-b border-gray-100 px-4 py-3 last:border-0">
 <div className="h-4 w-16 rounded bg-subtle" />
 {Array.from({ length: cols }).map((_, c) => {
 const showGreen = SKELETON_PATTERN[(r * cols + c) % SKELETON_PATTERN.length];
 return (
 <div key={c} className="flex-1 flex justify-center">
 {showGreen ? (
 <div className="h-7 w-16 rounded-lg bg-green-100 dark:bg-green-900/40" />
 ) : (
 <div className="h-4 w-4 rounded bg-subtle" />
 )}
 </div>
 );
 })}
 </div>
 ))}
 </div>
 </div>
 );
}

function formatDatePresetLabel(preset: DatePreset): string {
 if (preset === "this-week") return "This week";
 if (preset === "next-week") return "Next week";
 return "Next 2 weeks";
}
